import { SystemStyleObject, Text } from "@chakra-ui/react";
import {
  AsyncSelect,
  FormatOptionLabelMeta,
  MenuPlacement,
  MultiValue,
  PropsValue,
  SingleValue,
} from "chakra-react-select";
import {
  InternalAdminDistributionGroupFragment,
  InternalAdminDistributionGroupListQuery,
  InternalAdminDistributionGroupListQueryVariables,
  InternalAdminTagFragment,
  InternalAdminTagListQuery,
  InternalAdminTagListQueryVariables,
  InternalAdminUserFragment,
  InternalAdminUserListQuery,
  InternalAdminUserListQueryVariables,
  UserRoleType,
} from "gql/graphql";
import debounce from "lodash/debounce";

import React, { memo, useCallback, useEffect, useState } from "react";
import colors from "app/colors";
import {
  CALLOUT_KEY,
  CALLOUT_TYPE,
  INTERACTIVE_USER_ROLE_TYPE_LIST,
} from "modules/utils/Constants";
import {
  getDistributionGroupMention,
  getTagMention,
  getUserFullName,
  isMultiValue,
} from "modules/utils/Functions";
import useBurstQuery from "modules/hooks/use-burst-query";
import useQueryString from "modules/hooks/use-query-string";
import { ApolloQueryResult } from "@apollo/client";
import InternalAdminTagOptionLabel from "../selectors/internal-admin-tag-option-label";
import InternalAdminDistributionGroupOptionLabel from "../selectors/internal-admin-distribution-group-option-label";
import InternalAdminUserOptionLabel from "../selectors/internal-admin-user-option-label";
import InternalAdminEmailUserOptionLabel from "../selectors/internal-admin-email-user-option-label";
import InternalAdminSelectedUserOption from "../selectors/internal-admin-selected-user-option";
import INTERNAL_ADMIN_DISTRIBUTION_GROUP_LIST_QUERY from "../_networking/internal-admin-distribution-group-list";
import INTERNAL_ADMIN_TAG_LIST_QUERY from "../_networking/internal-admin-tag-list";
import INTERNAL_ADMIN_USER_LIST_QUERY from "../_networking/internal-admin-user-list";

interface IProps {
  onSelectedOptionsChanged: (options: Option[]) => void;
  types: CALLOUT_TYPE[];
  menuPlacement?: MenuPlacement;
  defaultValues?: Option[];
  value?: Option[]; //allows controlled usage
  isMultiSelect: boolean;
  placeHolder?: string;
  includeEmailUsers?: boolean;
  allowInactive?: boolean; //allows inactive users, tags, and distribution groups to be selected
  excludedIds?: string[]; // IDs to exclude from selection
}

export interface Option {
  id: string;
  type: CALLOUT_TYPE;
  active: boolean;
  data:
    | InternalAdminUserFragment
    | InternalAdminDistributionGroupFragment
    | InternalAdminTagFragment;
}

const InternalAdminUserTagGroupSelector = ({
  onSelectedOptionsChanged,
  types,
  menuPlacement = "bottom",
  defaultValues,
  isMultiSelect,
  placeHolder = "Click to add",
  value,
  includeEmailUsers = false,
  allowInactive = true,
  excludedIds = [],
}: IProps) => {
  const { qsOrganizationId } = useQueryString();
  const [search, setSearch] = useState("");
  const [_value, setValue] = useState<PropsValue<Option>>();

  const [internalAdminUserListQuery] = useBurstQuery<
    InternalAdminUserListQuery,
    InternalAdminUserListQueryVariables
  >(INTERNAL_ADMIN_USER_LIST_QUERY);

  const [internalAdminTagListQuery] = useBurstQuery<
    InternalAdminTagListQuery,
    InternalAdminTagListQueryVariables
  >(INTERNAL_ADMIN_TAG_LIST_QUERY);

  const [internalAdminDistributionGroupListQuery] = useBurstQuery<
    InternalAdminDistributionGroupListQuery,
    InternalAdminDistributionGroupListQueryVariables
  >(INTERNAL_ADMIN_DISTRIBUTION_GROUP_LIST_QUERY);

  useEffect(() => {
    if (defaultValues) {
      onChange(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInputChanged = useCallback((newValue: string) => {
    setSearch(newValue);
  }, []);

  const formatOptionLabel = useCallback(
    (option: Option, { context }: FormatOptionLabelMeta<Option>) => {
      switch (option.type) {
        case "user": {
          const user = option.data as InternalAdminUserFragment;
          if (user.roleType === UserRoleType.Email) {
            return (
              <InternalAdminEmailUserOptionLabel user={user} search={search} />
            );
          }
          if (context === "value") {
            return (
              <InternalAdminSelectedUserOption user={user} search={search} />
            );
          }
          return <InternalAdminUserOptionLabel user={user} search={search} />;
        }
        case "tag": {
          return (
            <InternalAdminTagOptionLabel
              tag={option.data as InternalAdminTagFragment}
              search={search}
            />
          );
        }
        case "distribution-group": {
          return (
            <InternalAdminDistributionGroupOptionLabel
              distributionGroup={
                option.data as InternalAdminDistributionGroupFragment
              }
              search={search}
            />
          );
        }
        default:
          return <Text>not found</Text>;
      }
    },
    [search],
  );

  const getOptionLabel = useCallback((option: Option) => {
    switch (option.type) {
      case "user": {
        const user = option.data as InternalAdminUserFragment;
        if (user) {
          return `${CALLOUT_KEY}${user.callout} ${getUserFullName(user)}`;
        }
        return "";
      }
      case "tag": {
        const tag = option.data as InternalAdminTagFragment;
        if (tag) {
          return `${getTagMention(tag)}`;
        }
        return "";
      }
      case "distribution-group": {
        const distributionGroup =
          option.data as InternalAdminDistributionGroupFragment;
        if (distributionGroup) {
          return getDistributionGroupMention(distributionGroup);
        }
        return "";
      }
      default:
        return "";
    }
  }, []);

  const getOptionValue = useCallback((option: Option) => option.id, []);

  const _loadOptions = useCallback(
    (inputValue: string, callback: (options: Option[]) => void) => {
      if (!qsOrganizationId || types.length === 0) {
        callback([]);
        return;
      }

      const userQueryPromise: Promise<ApolloQueryResult<InternalAdminUserListQuery> | null> =
        types.includes("user")
          ? internalAdminUserListQuery({
              variables: {
                organizationId: qsOrganizationId,
                input: {
                  limit: 10,
                  offset: 0,
                  filter: inputValue,
                  active: allowInactive ? undefined : true,
                  roleTypeList: includeEmailUsers
                    ? undefined
                    : INTERACTIVE_USER_ROLE_TYPE_LIST,
                },
              },
            })
          : Promise.resolve(null);

      const tagQueryPromise: Promise<ApolloQueryResult<InternalAdminTagListQuery> | null> =
        types.includes("tag")
          ? internalAdminTagListQuery({
              variables: {
                organizationId: qsOrganizationId,
                input: {
                  limit: 10,
                  offset: 0,
                  filter: inputValue,
                  active: allowInactive ? undefined : true,
                },
              },
            })
          : Promise.resolve(null);

      const distributionGroupQueryPromise: Promise<ApolloQueryResult<InternalAdminDistributionGroupListQuery> | null> =
        types.includes("distribution-group")
          ? internalAdminDistributionGroupListQuery({
              variables: {
                organizationId: qsOrganizationId,
                input: {
                  limit: 10,
                  offset: 0,
                  filter: inputValue,
                  active: allowInactive ? undefined : true,
                },
              },
            })
          : Promise.resolve(null);

      try {
        const _options: Option[] = [];
        Promise.all([
          userQueryPromise,
          tagQueryPromise,
          distributionGroupQueryPromise,
        ]).then(([userResponse, tagResponse, distributionGroupResponse]) => {
          userResponse?.data.internalAdminUserList
            .filter((user) => !excludedIds.includes(user.id))
            .forEach((user) => {
              _options.push({
                id: user.id,
                type: "user",
                active: user.active,
                data: user,
              });
            });

          tagResponse?.data.internalAdminTagList
            .filter((tag) => !excludedIds.includes(tag.id))
            .forEach((tag) => {
              _options.push({
                id: tag.id,
                type: "tag",
                active: tag.active,
                data: tag,
              });
            });

          distributionGroupResponse?.data.internalAdminDistributionGroupList
            .filter((group) => !excludedIds.includes(group.id))
            .forEach((group) => {
              _options.push({
                id: group.id,
                type: "distribution-group",
                active: group.active,
                data: group,
              });
            });
          callback(_options);
        });
      } catch (error) {
        callback([]);
      }
    },
    [
      qsOrganizationId,
      types,
      internalAdminUserListQuery,
      allowInactive,
      includeEmailUsers,
      internalAdminTagListQuery,
      internalAdminDistributionGroupListQuery,
      excludedIds,
    ],
  );

  const loadOptions = debounce(_loadOptions, 300);

  const onChange = useCallback(
    (options: SingleValue<Option> | MultiValue<Option>) => {
      setValue(options);
      if (options === null) {
        onSelectedOptionsChanged([]);
        return;
      }
      if (isMultiValue(options)) {
        onSelectedOptionsChanged([...options]);
      } else {
        onSelectedOptionsChanged([
          {
            ...options,
          },
        ]);
      }
    },
    [onSelectedOptionsChanged],
  );

  return (
    <AsyncSelect
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      menuPlacement={menuPlacement}
      loadOptions={loadOptions}
      defaultOptions
      onInputChange={onInputChanged}
      onChange={onChange}
      placeholder={placeHolder}
      value={value ?? _value}
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
        }),
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "full",
          borderColor: colors.grayPrimary,
        }),
      }}
      isMulti={isMultiSelect}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      isClearable
    />
  );
};

export default memo(InternalAdminUserTagGroupSelector);
